import * as React from "react";
import { connect } from "react-redux";
import get from "lodash/get";

import {
  LedgerActivity as LedgerActivityComponent,
  LedgerActivityProps,
} from "../../components/LedgerActivity/LedgerActivity";
import { getLedgerActivityAction, getRewardsAction } from "../../actions";
import { StoreState } from "../../reducers";
import {
  getIsLoadingLedgerActivity,
  getLedgerActivity,
} from "../../reducers/loyalty/methods";
import { getWidgetConfig } from "../../reducers/methods";
import { getRewards, isGetRewardsLoading } from "../../reducers/reward/methods";
import { customerAuthenticationOccurred } from "../../utils/customer";
import { filterLoyaltyDashboardLedgerActivity } from "../MyRewards/filterLoyaltyRewards";

interface LedgerActivityContainerProps extends LedgerActivityProps {
  getLedgerActivityAction: typeof getLedgerActivityAction;
  getRewardsAction: typeof getRewardsAction;
  ledgerActivity: ReturnType<typeof getLedgerActivity>;
  // settings.rewardStoreWidgetId being set means this widget instance IS the
  // Reward Store widget—all activity is relevant there, so skip filtering.
  // When absent the widget is the Loyalty Dashboard; reward-store entries are
  // filtered out to keep it focused on loyalty-only activity.
  rewardStoreWidgetId?: string;
  rewardsLoading: boolean;
}

class LedgerActivity extends React.Component<LedgerActivityContainerProps> {
  private getLedgerCurrency = (): string | undefined => {
    const propCurrency = get(this.props, "currency");
    const configCurrency = get(this.props, "config.settings.ledgerCurrency");
    const currency =
      typeof propCurrency === "string" && propCurrency.trim()
        ? propCurrency
        : configCurrency;

    return typeof currency === "string" && currency.trim()
      ? currency.trim()
      : undefined;
  };

  private fetchData(authenticated: boolean) {
    if (!authenticated) {
      return;
    }
    this.props.getLedgerActivityAction({ currency: this.getLedgerCurrency() });
    // On the loyalty dashboard (no rewardStoreWidgetId) we filter ledger entries
    // using the rewards list. Fetch rewards here so this component is
    // self-sufficient even when the MyRewards widget is absent from the page.
    if (!this.props.rewardStoreWidgetId && !this.props.rewardsLoading) {
      this.props.getRewardsAction();
    }
  }

  componentDidMount() {
    this.fetchData(get(this.props.data, "customer.authenticated", false));
  }

  componentDidUpdate(prevProps: LedgerActivityContainerProps) {
    if (customerAuthenticationOccurred(prevProps.data, this.props.data)) {
      this.fetchData(true);
    }
  }

  render() {
    return <LedgerActivityComponent {...this.props} />;
  }
}

const mapStateToProp = (state: StoreState) => {
  const config = getWidgetConfig(state);
  const rewardStoreWidgetId = get(config, "settings.rewardStoreWidgetId") as
    | string
    | undefined;
  const rawLedgerActivity = getLedgerActivity(state.loyalty);
  const rewards = getRewards(state.reward);
  const ledgerActivity =
    rewardStoreWidgetId || rawLedgerActivity == null
      ? rawLedgerActivity
      : filterLoyaltyDashboardLedgerActivity(rawLedgerActivity, rewards ?? undefined);

  // Hold the loading state until rewards have also loaded: the ledger filter
  // depends on the rewards list to build its exclusion sets, so showing entries
  // before rewards arrive would cause a visible flash as reward-store rows
  // appear and then disappear once the filter can run properly.
  // Because this component self-fetches rewards (see fetchData), this gate is
  // guaranteed to resolve—it does not depend on MyRewards being on the page.
  return {
    isLoading:
      getIsLoadingLedgerActivity(state.loyalty) ||
      (!rewardStoreWidgetId && isGetRewardsLoading(state.reward)),
    ledgerActivity,
    rewardStoreWidgetId,
    rewardsLoading: isGetRewardsLoading(state.reward),
  };
};

export default connect(mapStateToProp, {
  getLedgerActivityAction,
  getRewardsAction,
})(LedgerActivity);
